import React, { useEffect, useRef, useContext, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import CenteredLogo from "./CenteredLogo/CenteredLogo.tsx";
import messageIdMapping from "../../Utils/IdMappings/MessageIdMapping.ts";
import sharedChatMessageIdMapping from "../../Utils/IdMappings/SharedChatMessageIdMapping.ts";
import { useAppStore } from "../../GlobalStore/GlobalStore.ts";
import ErrorMessage from "./ErrorMessage/ErrorMessage.tsx";
import Message from "./Message/Message.tsx";
import Suggestions from "./Suggestions/Suggestions.tsx";
import EmbeddingProgress from "./EmbeddingProgress/EmbeddingProgress.tsx";
import { GlobalContext } from '../../App';
import {initializeNewChat} from "../../Utils/db/chatHistory/chatMessagesUtils.ts";
import {MessageNode} from "../../Utils/messageObject/MessageNode.ts";
import {addChild, setupUserMessage} from "../../Utils/messageObject/messageObjectUtils.ts";
import {getBlurStyles} from "../../Utils/misc/cssStyleUtils.ts";

interface MiddleProps {
    rootNode: MessageNode;
    messagesSequence: number[];
    isPreview: boolean;
}

const Middle: React.FC<MiddleProps> = ({ rootNode, messagesSequence, isPreview }) => {
    const { handleSubmitHelper, currentMessageRef, messagesSequenceRef, setMessages, messagesRef, llmRequestRefs, userObjRef } = useContext(GlobalContext);
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const updateChatHistory = useAppStore((state: any) => state.updateChatHistory);
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const { isIncognito } = settingsObj;
    const { errorMessage, hasText, hasAttachment } = dynamicStateObj;
    const virtuosoRef = useRef<any>(null);
    const [isEmbeddingProgressVisible, setIsEmbeddingProgressVisible] = useState(true);

    const handleDismissEmbeddingProgress = () => {
        setIsEmbeddingProgressVisible(false);
    };

    const handlePromptClick = (prompt: { text: string; icon: React.ComponentType }) => {
        if (Object.keys(messageIdMapping).length === 0) {
            initializeNewChat(prompt.text, isIncognito, userObjRef.current.user, setDynamicStateObj, updateChatHistory, setMessages, messagesRef, currentMessageRef);
        }

        const content = [{
            type: 'text',
            text: prompt.text
        }];
        const userMessage = setupUserMessage(content);

        llmRequestRefs.current.parentMessage = currentMessageRef.current;
        addChild(userMessage, currentMessageRef, messagesSequenceRef);
        llmRequestRefs.current.messagesToWrite.push(currentMessageRef.current);
        handleSubmitHelper();
    };

    const scrollToBottom = () => {
        if (virtuosoRef.current) {
            setTimeout(() => {
                virtuosoRef.current.scrollToIndex({
                    index: "LAST",
                    align: 'end',
                    behavior: 'instant'
                });
            }, 100);
        }
    };

    useEffect(() => {
        if (dynamicStateObj.scrollToBottom) {
            scrollToBottom();
            setDynamicStateObj({ scrollToBottom: false });
        }
    }, [dynamicStateObj.scrollToBottom]);

    const flattenMessageTree = (
        node: MessageNode,
        index: number,
        flattenedMessages: any[] = [],
        parentSelectedChild: number | null = null,
        totalChildren: number | null = null,
        messageCount: number = 0
    ): any[] => {
        if (!node) return flattenedMessages;

        const createGroup = (id: string | null, message: any, metadata: any, groupIndex: number, groupRole: string) => ({
            messages: [{ message, metadata, id }],
            parentSelectedChild,
            totalChildren,
            index: groupIndex,
            groupRole,
        });

        if (node.message) {
            const role = node.message.role;
            if (role === 'user') {
                flattenedMessages.push(createGroup(node.id, node.message, node.metadata, messageCount, role));
                messageCount++;
            } else if (role === 'assistant') {
                if (
                    flattenedMessages.length > 0 &&
                    flattenedMessages[flattenedMessages.length - 1].messages[0].message.role === 'assistant'
                ) {
                    flattenedMessages[flattenedMessages.length - 1].messages.push({ message: node.message, metadata: node.metadata });
                } else {
                    flattenedMessages.push(createGroup(node.id, node.message, node.metadata, messageCount, 'assistant'));
                }
                messageCount++;
            } else if (role === 'tool') {
                flattenedMessages[flattenedMessages.length - 1].messages.push({ message: node.message, metadata: node.metadata });
                messageCount++;
            }
        }
        if (node.children.length !== 0 && index < messagesSequence.length) {
            const mappingObj = isPreview ? sharedChatMessageIdMapping : messageIdMapping;
            return flattenMessageTree(mappingObj[node.children[messagesSequence[index]]], index + 1, flattenedMessages, messagesSequence[index], node.children.length, messageCount);
        }
        return flattenedMessages;
    };



    const flattenedMessages = flattenMessageTree(rootNode, 0);

    // Append errorMessage as a special item in the flattenedMessages
    if (errorMessage && !isPreview) {
        flattenedMessages.push({ isErrorMessage: true, errorMessage });
    }


    const blurStyles = getBlurStyles([dynamicStateObj.isMenuOpen, dynamicStateObj.isUploadOpen, dynamicStateObj.visiblePopup?.includes("settings")]);

    return (
        <div className={`overflow-y-auto flex-1 break-words mt-3 ${blurStyles} relative`}>
            {flattenedMessages.length === 0 ? (
                <div className="flex flex-col h-full relative">
                    <div className="flex-1 flex items-center justify-center">
                        <CenteredLogo />
                    </div>
                    {/* Container for embedding progress and suggestions at bottom */}
                    <div className="absolute bottom-0 left-0 right-0 w-full flex flex-col">
                        {!hasText && !hasAttachment && isEmbeddingProgressVisible && (
                            <EmbeddingProgress className="mb-2" isPreview={isPreview} onDismiss={handleDismissEmbeddingProgress} />
                        )}
                        <Suggestions onPromptClick={handlePromptClick} />
                    </div>
                </div>
            ) : (
                <div className="mx-auto h-full pb-2 relative">
                    <Virtuoso
                        ref={virtuosoRef}
                        data={flattenedMessages}
                        followOutput="auto"
                        itemContent={(index: number, item: any) => {
                            if (item.isErrorMessage) {
                                return (
                                    <ErrorMessage errorMessage={item.errorMessage} />
                                );
                            } else {
                                return (
                                    <Message
                                        key={index}
                                        group={item}
                                        isPreview={isPreview}
                                        isLast={index === flattenedMessages.length - 1}
                                    />
                                );
                            }
                        }}
                    />
                </div>
            )}
            {/* Show embedding progress at the bottom when there are messages or user input */}
            {(flattenedMessages.length > 0 || hasText || hasAttachment) && isEmbeddingProgressVisible && (
                <div className="absolute bottom-2 left-0 right-0">
                    <EmbeddingProgress isPreview={isPreview} onDismiss={handleDismissEmbeddingProgress} />
                </div>
            )}
        </div>
    );
};

export default Middle;
